import * as amplitude from "@amplitude/analytics-browser";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
} from "react";
import { useFlowID } from "./FlowIDContext";
import { useConfig } from "../context/ConfigContext";

const AmplitudeContext = createContext();

export const useAmplitude = () => useContext(AmplitudeContext);

export const AmplitudeProvider = ({ children }) => {
  const flowId = useFlowID();
  const { configData } = useConfig();

  useEffect(() => {
    // Initialize the amplitude instance
    amplitude.init(process.env.REACT_APP_AMPLITUDE_API_KEY, {
      defaultTracking: true,
      autocapture: {
        attribution: true,
        pageViews: true,
        sessions: true,
        formInteractions: false,
        fileDownloads: false,
        elementInteractions: true,
      },
      logLevel: amplitude.Types.LogLevel.Error,
    });

    return () => {
      // Clean up amplitude
      amplitude.reset();
    };
  }, []);

  // Event tracking utility
  const pushAmplitudeEvent = (eventName, eventProperties = {}) => {
    if (process.env.REACT_APP_AMPLITUDE_API_KEY !== "") {
      amplitude.track(eventName, eventProperties);
    }
  };

  // Track event function
  const trackEvent = useCallback(
    (
      name,
      uid = "",
      lastPageName = "",
      email = "",
      productTitle = "",
      productUrl = "",
    ) => {
      // Check shop
      const queryParams = new URLSearchParams(window.location.search);
      const shopRegion = queryParams.get("shop") || "de";

      const eventProperties = {
        partnerName: `${configData.partnerName}-${shopRegion}`,
      };

      // check if flowId is valid
      eventProperties.flowId = flowId;

      // check if UID is there
      if (uid) {
        eventProperties.uid = uid;
      }

      if (email) {
        eventProperties.email = email;
      }

      // check for product click event
      if (productTitle) {
        eventProperties.productTitle = productTitle;
        eventProperties.productUrl = productUrl;
      }

      // Check for last Page visits
      if (lastPageName) {
        eventProperties.pageName = lastPageName;
      }

      pushAmplitudeEvent(name, eventProperties);
    },
    [flowId, configData.partnerName],
  );

  return (
    <AmplitudeContext.Provider value={trackEvent}>
      {children}
    </AmplitudeContext.Provider>
  );
};
